// src/components/OrganizationManagement/OrganizationActions.js
import React from 'react';
import PropTypes from 'prop-types';
import logger from '../../utils/logger';
import './OrganizationActions.css';

/**
 * OrganizationActions component for displaying action buttons
 * Handles edit, authorize/deauthorize, and delete actions
 */
const OrganizationActions = ({
  organization,
  canDelete,
  loading,
  userProfile,
  onEdit,
  onAuthorize,
  onUnauthorize,
  onDelete,
}) => {
  const shouldShowAuthorize = organization.romanworksAuthzStatus !== 'AUTHORIZED';
  
  // Check for Full Admin role - support both string array and object array formats
  const isFullAdmin = userProfile?.roles?.some(role => {
    const roleValue = typeof role === 'string' ? role : role?.name;
    return roleValue === 'Full Admin';
  });

  logger.log(
    `[Button Render] Org: ${organization.name}, Status: ${
      organization.romanworksAuthzStatus
    }, Show Authorize: ${shouldShowAuthorize}, UserRoles: ${JSON.stringify(
      userProfile?.roles
    )}, IsFullAdmin: ${isFullAdmin}`
  );

  return (
    <div className="actions-container">
      <div className="action-buttons-wrapper">
        {/* Edit/Update Button */}
        <button
          className="btn-edit"
          onClick={() => onEdit(organization)}
          disabled={loading}
          title="Update organization"
        >
          Update
        </button>

        {/* Authorize/Deauthorize Button */}
        {shouldShowAuthorize ? (
          <button
            className="btn-authorize"
            onClick={() => onAuthorize(organization)}
            disabled={loading}
            title="Authorize Romanworks integration for this organization"
          >
            Authorize
          </button>
        ) : (
          <span
            className="button-wrapper-with-tooltip"
            title={
              !isFullAdmin
                ? 'Only Full Admins can deauthorize an Organization for webhook events and M2M token authorization'
                : 'Revoke Romanworks integration authorization (Full Admin only)'
            }
          >
            <button
              className="btn-unauthorize"
              onClick={() => onUnauthorize(organization)}
              disabled={loading || !isFullAdmin}
            >
              Deauthorize
            </button>
          </span>
        )}

        {/* Delete Button */}
        <span
          className="button-wrapper-with-tooltip"
          title={
            !canDelete && isFullAdmin
              ? 'This organization cannot be deleted because it has at least one installation. Remove all installations to delete this organization.'
              : !canDelete && !isFullAdmin
              ? 'Requires Full Admin role to perform this action.'
              : 'Delete organization'
          }
        >
          <button
            className="btn-delete"
            onClick={() => onDelete(organization)}
            disabled={loading || !canDelete}
          >
            Delete
          </button>
        </span>
      </div>

      {/* Delete Disabled Note */}
      {!canDelete && (
        <div className="delete-disabled-note">(has sites, cannot be deleted)</div>
      )}
    </div>
  );
};

OrganizationActions.propTypes = {
  organization: PropTypes.shape({
    orgId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    romanworksAuthzStatus: PropTypes.string,
  }).isRequired,
  canDelete: PropTypes.bool.isRequired,
  loading: PropTypes.bool,
  userProfile: PropTypes.shape({
    roles: PropTypes.arrayOf(PropTypes.string),
  }),
  onEdit: PropTypes.func.isRequired,
  onAuthorize: PropTypes.func.isRequired,
  onUnauthorize: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

OrganizationActions.defaultProps = {
  loading: false,
  userProfile: null,
};

export default OrganizationActions;
